import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

function CustomTooltip({
  content,
  children,
  placement = "right",
  showDelayMs = 40,
  fadeDurationMs = 400,
  className = "",
  tooltipClassName = "",
  usePortal = false,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const triggerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const showTooltip = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
    }, Math.max(0, Number(showDelayMs) || 0));
  };

  const hideTooltip = () => {
    clearTimer();
    setIsVisible(false);
  };

  useEffect(() => () => clearTimer(), []);

  const updatePortalPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const offset = 8;
    let top = rect.top + rect.height / 2;
    let left = rect.right + offset;
    if (placement === "left") {
      left = rect.left - offset;
    } else if (placement === "top") {
      top = rect.top - offset;
      left = rect.left + rect.width / 2;
    } else if (placement === "bottom") {
      top = rect.bottom + offset;
      left = rect.left + rect.width / 2;
    }
    setPortalPosition({ top, left });
  };

  useEffect(() => {
    if (!usePortal || !isVisible) return undefined;
    updatePortalPosition();
    const handleReposition = () => updatePortalPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [usePortal, isVisible, placement]);

  if (!content) return children;

  const tooltipNode = (
    <span
      role="tooltip"
      className={`bb-custom-tooltip bb-custom-tooltip-${placement} ${
        isVisible ? "is-visible" : ""
      } ${tooltipClassName}`.trim()}
      style={{
        "--bb-tooltip-fade-ms": `${fadeDurationMs}ms`,
        ...(usePortal
          ? {
              position: "fixed",
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              right: "auto",
              bottom: "auto",
              transform:
                placement === "right"
                  ? "translate(0, -50%)"
                  : placement === "left"
                    ? "translate(-100%, -50%)"
                    : placement === "top"
                      ? "translate(-50%, -100%)"
                      : "translate(-50%, 0)",
              zIndex: 4000,
            }
          : {}),
      }}
    >
      {content}
    </span>
  );

  return (
    <span
      ref={triggerRef}
      className={`bb-custom-tooltip-wrap ${className}`.trim()}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {usePortal && typeof document !== "undefined"
        ? createPortal(tooltipNode, document.body)
        : tooltipNode}
    </span>
  );
}

export default CustomTooltip;
